"use client";

import React, { useState, useEffect } from 'react';
import CustomSlider from '../ui/CustomSlider/CustomSlider';
import Link from 'next/link';
import ImageWrapper from '../ui/ImageWrapper';
import { formatPrice, safeImageUrl } from '@/app/utilities/helper';
import LeadButton from '../ui/Lead/LeadButton';
import Image from 'next/image';
import ViewAllButton from '../ui/ViewAllButton';
import { HeadingProps } from '@/app/utilities/interfaces';

interface Brand {
	_id: string;
	name: string;
	slug: string;
	image?: string;
}

interface ElectricItem {
	_id: string;
	name: string;
	slug: string;
	brand?: Brand;
	featured_image?: string;
	images?: string[];
	price_range?: { base?: number; top?: number };
	brand_name?: string;
	brand_slug?: string;
	brand_id?: string;
}

interface ElectricCarsSliderProps {
	data?: ElectricItem[];
	trackingClass?: string;
	isMobile?: boolean;
	titleTag?: HeadingProps["tag"];
}

export default function ElectricCarsSlider({ data, trackingClass, isMobile = false, titleTag = "h3" }: ElectricCarsSliderProps) {
	const [isAtStart, setIsAtStart] = useState(true);
	const [isAtEnd, setIsAtEnd] = useState(false);
	const prevRef = React.useRef<HTMLButtonElement | null>(null);
	const nextRef = React.useRef<HTMLButtonElement | null>(null);

	const items = (data ?? []).map((it) => {
		const brandName = it?.brand?.name || it.brand_name || '';
		const brandSlug = it?.brand?.slug || it.brand_slug || '';
		const featured = it.featured_image || (it.images && it.images[0]) || '';
		const priceRange = it.price_range ?? { base: 0, top: 0 };

		return {
			brand: {
				_id: it?.brand?._id || it.brand_id || '',
				name: brandName,
				slug: brandSlug,
				image: it?.brand?.image,
			},
			model: { _id: it._id, name: it.name, slug: it.slug, featured_image: featured },
			price_range: priceRange,
			raw: it,
		};
	});

	if (!items.length) return null;

	const showNavigation = items.length > 3;

	return (
		<section id="electric-cars" className="mb-4 md:mb-8">
			<p className="text-[20px] font-bold heading-p leading-[36px]">Electric Cars</p>
			<p className="text-[14px] leading-[24px] text-gray-600 mb-6">Handpicked electric alternatives</p>

			<div className="relative">
				{showNavigation && (
					<>
						<button
							ref={prevRef}
							type="button"
							aria-label="Previous"
							disabled={isAtStart}
							className={`hidden sm:flex items-center justify-center absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full shadow focus:outline-none z-10 bg-teal-700 text-white ${isAtStart ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
						>
							<Image
								src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronLeft.svg`}
								alt="ChevronLeft Previous"
								width={8}
								height={14}
								title="ChevronLeft Previous"
								/>
						</button>
						<button
							ref={nextRef}
							type="button"
							aria-label="Next"
							disabled={isAtEnd}
							className={`hidden sm:flex items-center justify-center absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full shadow focus:outline-none z-10 bg-teal-600 text-white ${isAtEnd ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
						>
							<Image
								src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg`}
								alt="ChevronRight Next"
								width={8}
								height={14}
								title="ChevronRight Next"
								/>
						</button>
					</>
				)}

				<CustomSlider
					responsive={{ mobile: 1.2, tablet: 2, desktop: 3 }}
					// IMPORTANT: keep first render deterministic for SSR hydration.
					// `CustomSlider` will adjust `visibleItems` on mount via `responsive`.
					itemsToShow={isMobile ? 1.2 : 3}
					showArrows={showNavigation}
					gap={16}
				>
					{items.map((car) => (
						<div key={car.model._id}><ElectricCarCard car={car} trackingClass={trackingClass} titleTag={titleTag} /></div>
					))}
				</CustomSlider>
			</div>

			{/* <ViewAllLink href="/electric-cars" label="View All Electric Cars" title="View All Electric Cars" /> */}
			<div className="mt-3 text-center">
				<ViewAllButton
					href="/electric-cars"
					title="View All Electric Cars"
					className="flex !rounded-[12px] !bg-[#FBFCFD] !border !border-[#EDF0F3]  !text-[#343941] !text-[12px] inline-flex mb-4"
					iconClass="invert"
				/>
			</div>

		</section>
	);
}

function ElectricCarCard({ car, trackingClass, titleTag = "h3" }: { car: any, trackingClass?: string; titleTag?: HeadingProps["tag"] }) {
	const TitleTag = titleTag;
	const brand = car.brand?.name || '';
	const model = car.model?.name || '';
	const title = `${brand} ${model}`.trim();
	const href = `/${car.brand?.slug}/${car.model?.slug}`;

	const imageUrl = safeImageUrl(car.model?.featured_image || car.model?.image || '');
	const basePrice = Number((car as any)?.price_range?.base ?? 0) || 0;
	const priceDisplay = basePrice > 0 ? String(formatPrice(basePrice, { includeUnit: true })) : null;

	return (
		<div className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden h-full flex flex-col mr-1`}>
			<Link prefetch={false} href={href} className="block" title={title}>
				<div className={`w-full h-48 bg-gray-50`}>
					<ImageWrapper
						src={imageUrl}
						alt={`${title}`}
						width={450}
						height={220}
						className="w-full h-full object-cover"
						loading="lazy"
						title={`${title}`}
					/>
				</div>
			</Link>

			<div className={`p-4 flex flex-col flex-grow`}>
				<div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">{brand}</div>
				<Link prefetch={false} href={href} className="block" aria-label={model} title={model}>
					<TitleTag className={`text-[16px] text-gray-900 mt-1 line-clamp-2`}>{model}</TitleTag>
				</Link>
				{priceDisplay ? (
					<p className={`text-[20px] text-[#101828] mt-1`}>
						{priceDisplay}<span className="text-[11px] text-[#62748E] font-normal ml-0.5"> onwards</span>
					</p>
				) : null}

				<div className={`mt-4`}>
					<LeadButton
						carId={car.model._id}
						carName={title}
						btnText={<span className="flex items-center justify-center gap-2">Check Offers
						<Image
							src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg`}
							alt="ChevronRight Next"
							width={6}
							height={12}
							title="ChevronRight Next"
							/>
						</span>}
						className={`w-full py-2.5 text-[16px] leading-[24px] text-white bg-[#008C8B] rounded-lg hover:opacity-90 transition ${trackingClass}`}
						is_upcoming={car.model?.is_upcoming}
					/>
				</div>
			</div>
		</div>
	);
}
